import { createI18n } from 'vue-i18n';
import Cookies from 'js-cookie';
import { nonAuthData } from '@/api/apiService';
import { META_TRANSLATIONS, DOWNLOAD_TRANSLATION } from '@/api/apiRoute';

import en from './locales/en.json';
import pt from './locales/pt.json';

// ─── Error reporting ──────────────────────────────────────────────────────────
// Centralised error logger. In production, swap the console.error call for your
// Sentry (or equivalent) integration:
//
//   import * as Sentry from '@sentry/vue';
//   Sentry.captureException(error, { extra: context });
//
// Keeping it as a wrapper means you only need to change one place.
const reportError = (message, error, context = {}) => {
  if (import.meta.env.PROD) {
    // Production: send to Sentry (uncomment when Sentry is configured)
    // Sentry.captureException(error, {
    //   extra: { message, ...context },
    //   tags: { module: 'i18n' },
    // });
    console.error(`[i18n] ${message}`, context, error);
  } else {
    // Development: verbose logging
    console.error(`[i18n] ${message}`, context, error);
  }
};

// Retry utility with exponential backoff
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      // Don't retry on 4xx client errors (except 429 Too Many Requests)
      if (error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 429) {
        throw error;
      }
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff: baseDelay * 2^(attempt-1) with jitter
      const delay = baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      console.warn(`Translation API attempt ${attempt} failed, retrying in ${Math.round(delay)}ms:`, error.message);
    }
  }
};

// Simple merge utility that preserves local translations
function mergeTranslations(local, remote) {
  try {
    const merged = { ...local };
    if (!remote) return merged;

    Object.keys(remote).forEach(key => {
      const remoteVal = remote[key];
      
      // Always preserve local modules keys - don't overwrite them with remote data
      if (key === 'modules') {
        return; // Skip modules entirely, keep local version
      }
      
      // If it's a nested object, recurse
      if (remoteVal && typeof remoteVal === 'object' && !Array.isArray(remoteVal)) {
        merged[key] = mergeTranslations(merged[key] || {}, remoteVal);
      } else {
        // If it's a value, only use it if:
        // 1. Local doesn't have it
        // 2. Remote value is NOT a technical key (doesn't contain dots and common prefixes)
        const isPlaceholder = typeof remoteVal === 'string' && (
          remoteVal.trim() === key.trim() || 
          // Catch cases where remote data is nested but the value is the full dot-path placeholder
          remoteVal.endsWith(`.${key}`)
        );
        
        const isTechnical = typeof remoteVal === 'string' && 
          (remoteVal.includes('templateFields.') || remoteVal.includes('templates.'));
        
        // Never use remote placeholders (they corrupt local nested translations if sent as flat keys or nested paths)
        if (isPlaceholder) {
          return;
        }
        
        // Always preserve local translations if they exist, only use remote if local is missing
        if (!merged[key]) {
          merged[key] = remoteVal;
        }
        // For technical keys, only use remote if it's NOT a placeholder (already handled above)
        else if (isTechnical) {
          merged[key] = remoteVal;
        }
        // For non-technical keys, keep local (preserve user translations)
      }
    });
    return merged;
  } catch (error) {
    reportError('Translation merge failed', error, { localKeys: Object.keys(local || {}), remoteKeys: Object.keys(remote || {}) });
    // Return local on merge failure to ensure app continues working
    return local;
  }
}

const savedLanguage = Cookies.get('language') || 'en';

// ─── Remote translation version cache ────────────────────────────────────────
// Stores the last-seen remote version tag per locale so we can invalidate
// the i18n message cache when the server publishes a new translation version.
const REMOTE_VERSION_KEY = (lang) => `i18n_remote_version_${lang}`;

function getStoredRemoteVersion(lang) {
  try { return localStorage.getItem(REMOTE_VERSION_KEY(lang)) || null; } catch { return null; }
}

function setStoredRemoteVersion(lang, version) {
  try { localStorage.setItem(REMOTE_VERSION_KEY(lang), version); } catch { /* storage full */ }
}

// ─── i18n instance ────────────────────────────────────────────────────────────
const i18n = createI18n({
  legacy: false,
  locale: savedLanguage,
  fallbackLocale: 'en',
  messages: { en, pt },
  missingWarn: false,
  fallbackWarn: false,
  // In development, log every missing translation key to the console so gaps
  // are caught early. Disabled in production to keep the console clean.
  missing: import.meta.env.DEV
    ? (locale, key) => {
        // Suppress noisy vue-i18n internal fallback probes (keys ending with
        // a numeric index are interpolation artefacts, not real missing keys).
        if (!key || /\.\d+$/.test(key)) return;
        console.warn(`[i18n] Missing key "${key}" for locale "${locale}"`);
      }
    : undefined,
});

export const loadLanguage = async (lang = savedLanguage) => {
  try {
    const targetLang = lang || 'en';
    const localMessages = targetLang === 'en' ? en : pt;
    
    // 1. Immediate switch to local version so UI works right away
    try {
      i18n.global.setLocaleMessage(targetLang, localMessages);
      i18n.global.locale.value = targetLang;
      Cookies.set('language', targetLang);
    } catch (error) {
      reportError('Failed to apply local translation bundle', error, { lang: targetLang });
      // Non-fatal: continue to attempt remote fetch
    }

    // 2. Fetch remote translation metadata with retry logic
    let languages = [];
    try {
      const response = await retryWithBackoff(() => nonAuthData.get(META_TRANSLATIONS));
      languages = response?.data?.data || [];
    } catch (error) {
      reportError('Failed to fetch translation metadata after retries', error, { lang: targetLang });
      return; // Cannot proceed without metadata — local bundle already applied above
    }

    const langMeta = languages.find(l => l.languageCode === targetLang);
    
    if (langMeta) {
      // Check if remote version has changed since last fetch
      const remoteVersion = langMeta.version || langMeta.updatedAt || 'unknown';
      const storedVersion = getStoredRemoteVersion(targetLang);
      
      // Only fetch if version changed or no stored version exists
      if (remoteVersion !== storedVersion) {
        let remoteData = null;
        try {
          const translationRes = await retryWithBackoff(() => nonAuthData.get(DOWNLOAD_TRANSLATION(targetLang)));
          remoteData = translationRes.data?.data;
        } catch (error) {
          reportError('Failed to download remote translations after retries', error, { lang: targetLang, version: remoteVersion });
          return; // Local bundle already applied — safe to return
        }

        if (remoteData) {
          try {
            // Normalize: Wrap flat API data if needed
            let normalizedRemote = remoteData;
            if (!remoteData.templateFields && (remoteData.isMarried || remoteData.Account)) {
              normalizedRemote = { templateFields: remoteData };
            }

            // 3. Merge and update
            const finalMessages = mergeTranslations(localMessages, normalizedRemote);
            i18n.global.setLocaleMessage(targetLang, finalMessages);
            
            // Store the new version so we don't re-fetch until it changes
            setStoredRemoteVersion(targetLang, remoteVersion);
          } catch (error) {
            reportError('Failed to merge or apply remote translations', error, { lang: targetLang, version: remoteVersion });
            // Local bundle already applied — app continues working
          }
        }
      }
    }
  } catch (error) {
    // Outer catch: unexpected errors not caught by inner blocks
    reportError('Unexpected error in loadLanguage', error, { lang });
  }
};

// ─── Cache invalidation ───────────────────────────────────────────────────────
// Clears stale localStorage i18n artifacts if the in-memory messages are missing
// critical keys (e.g., 'modules'). This is a defensive measure for corrupted state.
// Also clears cache when the locale changes to prevent stale data from persisting.
const clearCacheIfNeeded = () => {
  const currentMessages = i18n.global.getLocaleMessage(savedLanguage);
  
  // Only clear cache if modules keys are missing (indicates corrupted state)
  if (!currentMessages.modules) {
    clearI18nCache();
  }
};

function clearI18nCache() {
  const keysToRemove = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('i18n') || key.includes('translation'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    // localStorage access can fail in private browsing mode or when storage is full
    console.warn('Failed to clear i18n cache:', e);
  }
}

// Watch for locale changes and clear cache to prevent stale translations
// This ensures that when the user switches language, any cached remote data
// from the previous locale is invalidated.
let previousLocale = i18n.global.locale.value;
setInterval(() => {
  const currentLocale = i18n.global.locale.value;
  if (currentLocale !== previousLocale) {
    clearI18nCache();
    previousLocale = currentLocale;
  }
}, 500); // Check every 500ms (lightweight polling since watch() isn't available at module level)

// Load language with cache management
clearCacheIfNeeded();
loadLanguage(savedLanguage);

export default i18n;
